import React, { useState, useEffect, useRef } from "react";
import { AvatarChip, PHOTO_AVATARS } from "./AvatarChip";
import { useAuth } from "../context/AuthContext";
import { updateProfile, fetchGames, generateAiAvatar, fetchUserTeams, changeDisplayName, toggleNameHistoryVisibility, createGame } from "../lib/api";
import { isProfileComplete } from "../lib/profile";
import { InboxPanel } from "./InboxPanel";
import { GloryBadgesGrid } from "./GloryBadgesGrid";
import { NotificationSettings } from "./NotificationSettings";
import { SupportTicketsPanel } from "./SupportTicketsPanel";
import PrivacySection from "./PrivacySection";

export function ProfilePanel({ open, onClose, onSaved, onRequireUpgrade, onboarding, initialTab, onToast }) {
  const { user, setUser } = useAuth();
  const [tab, setTab] = useState("avatar");
  const [bio, setBio] = useState(user?.bio || "");
  const [game, setGame] = useState(user?.game || "League of Legends");
  const [userType, setUserType] = useState(user?.user_type || "casual");
  const [social, setSocial] = useState(user?.social || "");
  const [discordHandle, setDiscordHandle] = useState(user?.discord_handle || "");
  const [xHandle, setXHandle] = useState(user?.x_handle || "");
  const [telegramHandle, setTelegramHandle] = useState(user?.telegram_handle || "");
  const [twitchHandle, setTwitchHandle] = useState(user?.twitch_handle || "");
  const [steamHandle, setSteamHandle] = useState(user?.steam_handle || "");
  const [battlenetHandle, setBattlenetHandle] = useState(user?.battlenet_handle || "");
  const [riotHandle, setRiotHandle] = useState(user?.riot_handle || "");
  const [psnHandle, setPsnHandle] = useState(user?.psn_handle || "");
  const [xboxHandle, setXboxHandle] = useState(user?.xbox_handle || "");
  const [epicHandle, setEpicHandle] = useState(user?.epic_handle || "");
  // Per-field validation errors. Populated when the backend returns a
  // 422 — Pydantic's `detail` is a list of `{loc:[…, field], msg}` so
  // we key by the field name and clear an entry the moment the user
  // edits that field (see `onPlatformChange` below).
  const [platformErrors, setPlatformErrors] = useState({});
  const [bannerUrl, setBannerUrl] = useState(user?.banner_url || "");
  const [websiteUrl, setWebsiteUrl] = useState(user?.website_url || "");
  const [youtubeUrl, setYoutubeUrl] = useState(user?.youtube_url || "");
  const [kickUrl, setKickUrl] = useState(user?.kick_url || "");
  const [streamerMode, setStreamerMode] = useState(!!user?.streamer_mode_enabled);
  const [avatarId, setAvatarId] = useState(user?.avatar_id ?? null);
  const [pfpUrl, setPfpUrl] = useState(user?.pfp_url || null);
  const [gamesPlayed, setGamesPlayed] = useState(user?.games_played || []);
  const [messagingEnabled, setMessagingEnabled] = useState(user?.messaging_enabled !== false);
  const [messagingPolicy, setMessagingPolicy] = useState(
    user?.messaging_policy
    || (user?.messaging_enabled === false ? "off" : "everyone")
  );
  // ---- Self-attestation badges (Phase 43) --------------------------------
  // Two opt-in cosmetic toggles that surface a small chip on the player's
  // profile card. Mutually exclusive: only one can be active at a time.
  const [isCertifiedDad, setIsCertifiedDad] = useState(!!user?.is_certified_dad);
  const [isCertifiedMom, setIsCertifiedMom] = useState(!!user?.is_certified_mom);
  const [saving, setSaving] = useState(false);
  const [games, setGames] = useState([]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [myTeams, setMyTeams] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => { fetchGames().then(setGames); }, []);

  useEffect(() => {
    if (!open || !user?.user_id) return;
    fetchUserTeams(user.user_id).then(setMyTeams).catch(() => setMyTeams([]));
  }, [open, user?.user_id]);

  useEffect(() => {
    if (open && user) {
      setBio(user.bio || "");
      setGame(user.game || "League of Legends");
      setUserType(user.user_type || "casual");
      setSocial(user.social || "");
      setDiscordHandle(user.discord_handle || "");
      setXHandle(user.x_handle || "");
      setTelegramHandle(user.telegram_handle || "");
      setTwitchHandle(user.twitch_handle || "");
      setSteamHandle(user.steam_handle || "");
      setBattlenetHandle(user.battlenet_handle || "");
      setRiotHandle(user.riot_handle || "");
      setPsnHandle(user.psn_handle || "");
      setXboxHandle(user.xbox_handle || "");
      setEpicHandle(user.epic_handle || "");
      setBannerUrl(user.banner_url || "");
      setWebsiteUrl(user.website_url || "");
      setYoutubeUrl(user.youtube_url || "");
      setKickUrl(user.kick_url || "");
      setStreamerMode(!!user.streamer_mode_enabled);
      setAvatarId(user.avatar_id ?? null);
      setPfpUrl(user.pfp_url || null);
      setMessagingEnabled(user.messaging_enabled !== false);
      setMessagingPolicy(
        user.messaging_policy
        || (user.messaging_enabled === false ? "off" : "everyone")
      );
      setIsCertifiedDad(!!user.is_certified_dad);
      setIsCertifiedMom(!!user.is_certified_mom);
      setTab(initialTab || "avatar");
    }
  }, [open, user, initialTab]);

  if (!open || !user) return null;

  const isPro = user.user_type === "pro" || user.plan === "pro" || user.plan === "elite";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        avatar_id: avatarId,
        pfp_url: pfpUrl,
        bio,
        game,
        games_played: gamesPlayed,
        user_type: userType,
        messaging_enabled: messagingPolicy !== "off",
        messaging_policy: messagingPolicy,
        social,
        discord_handle: discordHandle,
        x_handle: xHandle,
        telegram_handle: telegramHandle,
        twitch_handle: twitchHandle,
        steam_handle: steamHandle,
        battlenet_handle: battlenetHandle,
        riot_handle: riotHandle,
        psn_handle: psnHandle,
        xbox_handle: xboxHandle,
        epic_handle: epicHandle,
        website_url: websiteUrl,
        youtube_url: youtubeUrl,
        kick_url: kickUrl,
        streamer_mode_enabled: streamerMode,
        is_certified_dad: isCertifiedDad,
        is_certified_mom: isCertifiedMom,
      };
      // Banner is PRO-only — never send a non-empty value from a free user.
      if (isPro) payload.banner_url = bannerUrl;
      const updated = await updateProfile(payload);
      setUser(updated);
      onSaved && onSaved("Profile saved. Ready to drop in.");
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const field = e?.response?.data?.field;
      // The backend's global validation handler returns either
      //   { detail: "Steam Handle: …", field: "steam_handle" }
      // OR (older path) the raw Pydantic detail array. Handle both.
      if (Array.isArray(detail)) {
        const fieldErrors = {};
        for (const d of detail) {
          const fname = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : null;
          if (fname && /_handle$/.test(fname)) {
            fieldErrors[fname] = d?.msg?.replace(/^Value error,\s*/i, "") || "Invalid value";
          }
        }
        if (Object.keys(fieldErrors).length) {
          setPlatformErrors(fieldErrors);
          onSaved && onSaved("Fix highlighted handles and try again.");
        } else {
          onSaved && onSaved("Save failed. Try again.");
        }
      } else if (typeof field === "string" && /_handle$/.test(field)) {
        setPlatformErrors({ [field]: detail || "Invalid value" });
        onSaved && onSaved("Fix highlighted handle and try again.");
      } else {
        onSaved && onSaved(detail || "Save failed. Try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFile = (e) => {
    if (!isPro) {
      onRequireUpgrade && onRequireUpgrade();
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      onSaved && onSaved("Image too large (max 1.5 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPfpUrl(reader.result);
      setAvatarId(null);
    };
    reader.readAsDataURL(file);
  };

  const handleAiGen = async () => {
    if (!isPro) {
      onRequireUpgrade && onRequireUpgrade();
      return;
    }
    if (!aiPrompt.trim()) {
      onSaved && onSaved("Describe your vibe first.");
      return;
    }
    setAiBusy(true);
    try {
      const res = await generateAiAvatar(aiPrompt, "cyberpunk neon pixel art esports avatar");
      setPfpUrl(res.pfp_url);
      setAvatarId(null);
      // Refresh user to reflect server-side update
      setUser({ ...user, pfp_url: res.pfp_url, avatar_id: null });
      onSaved && onSaved("AI avatar minted.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI generation failed.";
      onSaved && onSaved(msg);
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-md p-2 sm:p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="profile-panel-backdrop"
    >
      <div
        className="glass neon-glow-pink rounded-3xl p-4 sm:p-8 w-full max-w-3xl max-h-[94vh] max-h-[94dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="profile-panel"
      >
        <div className="flex items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <AvatarChip avatarId={avatarId} pfpUrl={pfpUrl} size={64} />
            <div>
              <p className="text-xs tracking-[3px] text-[#14b8a6]">OPERATOR PROFILE</p>
              <h2 className="text-3xl font-display grad-text">{user.name}</h2>
              <p className="text-xs text-[#a3a3ac]">
                Signed in via {user.provider}
                {isPro && <span className="ml-2 text-[#14b8a6] font-bold">• PRO</span>}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="profile-close-btn"
            className="text-white/60 hover:text-white text-2xl"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* MY TEAMS — squads I'm on across leagues */}
        {myTeams.length > 0 && (
          <div data-testid="profile-my-teams" className="mb-5">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
              <i className="fas fa-shield-halved mr-1" />MY TEAMS · {myTeams.length}
            </p>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto pr-1">
              {myTeams.map((t) => (
                <span
                  key={t.team_id}
                  data-testid={`profile-team-${t.team_id}`}
                  title={`${t.name} · ${t.league?.name || ""}${t.is_manager ? " · MANAGER" : ""}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/40 border ${
                    t.is_manager ? "border-[#14b8a6]/50 text-[#14b8a6]" : "border-[#14b8a6]/30 text-[#a3a3ac]"
                  }`}
                >
                  <AvatarChip avatarId={t.avatar_id} size={20} />
                  <span className="text-xs font-bold truncate max-w-[140px]">{t.name}</span>
                  {t.is_manager && (
                    <span className="text-[9px] tracking-widest font-bold text-[#14b8a6]">
                      <i className="fas fa-crown mr-1" />MGR
                    </span>
                  )}
                  <span className="text-[10px] text-white/50">MMR {t.mmr || 0}</span>
                  {t.league?.slug && (
                    <a
                      href={`/${t.league.slug}`}
                      className="text-[10px] text-[#14b8a6] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ↗
                    </a>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Onboarding banner — only when profile hasn't been set up yet */}
        {onboarding && !isProfileComplete(user) && (
          <div
            data-testid="profile-onboarding-banner"
            className="mb-5 rounded-2xl p-4 border border-[#14b8a6]/50 bg-gradient-to-r from-[#14b8a6]/15 to-[#14b8a6]/10"
          >
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">STEP 2 / 3 • SET UP YOUR OPERATOR</p>
            <p className="text-sm text-white/85 mt-1">
              Pick an avatar (or generate one) and set your primary game. After that, you can
              create or join leagues.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10 pb-3 flex-wrap">
          {["avatar", "details", "skill", "social", "alerts", "glory", "inbox", "privacy", "help"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`profile-tab-${t}`}
              className={`px-4 py-2 rounded-2xl text-sm font-bold tracking-wider ${
                tab === t ? "bg-[#14b8a6] text-black" : "bg-white/5 text-white/70 hover:text-white"
              }`}
            >
              {t.toUpperCase()}
            </button>
          ))}
        </div>

        {tab === "avatar" && (
          <div data-testid="profile-tab-content-avatar" className="space-y-6">
            <p className="text-sm text-[#a3a3ac]">Pick a preset, upload a photo (PRO), or generate one with AI (PRO).</p>

            <div>
              <p className="text-xs tracking-widest text-[#14b8a6] mb-3">12 PRESETS</p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                {Array.from({ length: 12 }).map((_, i) => (
                  <AvatarChip
                    key={i}
                    avatarId={i}
                    size={96}
                    selected={avatarId === i && !pfpUrl}
                    onClick={() => { setAvatarId(i); setPfpUrl(null); }}
                    dataTestid={`avatar-option-${i}`}
                  />
                ))}
              </div>
            </div>

            {/* SUGGESTED OPERATIVES — full-resolution cyberpunk portraits
                pushed by the user. IDs continue right after the 12 pixel
                presets (12, 13, 14, ...) so existing saved avatar_ids
                stay valid and new picks land in the same column. */}
            <div data-testid="profile-tab-operatives">
              <div className="flex items-baseline justify-between mb-3">
                <p className="text-xs tracking-widest text-[#14b8a6]">
                  SUGGESTED OPERATIVES · {PHOTO_AVATARS.length}
                </p>
                <p className="text-[10px] text-white/40">
                  Hooded portraits · more drops soon
                </p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {PHOTO_AVATARS.map((opt, idx) => {
                  const id = 12 + idx; // see AvatarChip.PHOTO_AVATAR_OFFSET
                  return (
                    <div key={id} className="text-center">
                      <AvatarChip
                        avatarId={id}
                        size={120}
                        selected={avatarId === id && !pfpUrl}
                        onClick={() => { setAvatarId(id); setPfpUrl(null); }}
                        dataTestid={`avatar-option-${id}`}
                      />
                      <p className="mt-1.5 text-[10px] tracking-widest text-white/55 truncate">
                        {opt.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className={`glass rounded-2xl p-5 ${!isPro ? "opacity-80" : ""}`}>
                <p className="text-xs tracking-widest text-[#14b8a6] mb-2">
                  📷 UPLOAD PHOTO {!isPro && <span className="text-white/40">• PRO ONLY</span>}
                </p>
                <p className="text-sm text-white/70 mb-3">Real photo, max 1.5 MB. PRO gets the blue check.</p>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileRef}
                  onChange={handleFile}
                  data-testid="profile-upload-input"
                  className="hidden"
                />
                <button
                  onClick={() => isPro ? fileRef.current?.click() : onRequireUpgrade?.()}
                  data-testid="profile-upload-btn"
                  className={`w-full py-3 rounded-2xl font-bold ${isPro ? "btn-neon" : "btn-ghost"}`}
                >
                  {isPro ? "CHOOSE FILE" : "UNLOCK WITH PRO"}
                </button>
              </div>

              <div className={`glass rounded-2xl p-5 ${!isPro ? "opacity-80" : ""}`}>
                <p className="text-xs tracking-widest text-[#14b8a6] mb-2">
                  🤖 GENERATE WITH AI {!isPro && <span className="text-white/40">• PRO ONLY</span>}
                </p>
                <p className="text-sm text-white/70 mb-3">Gemini Nano Banana. Describe your vibe.</p>
                <input
                  type="text"
                  placeholder="e.g. cyberpunk samurai, magenta hair, visor"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  disabled={!isPro}
                  data-testid="profile-ai-prompt"
                  className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-2.5 text-white text-sm mb-3 disabled:opacity-50"
                />
                <button
                  onClick={handleAiGen}
                  disabled={aiBusy || !isPro}
                  data-testid="profile-ai-generate-btn"
                  className={`w-full py-3 rounded-2xl font-bold disabled:opacity-50 ${isPro ? "btn-neon" : "btn-ghost"}`}
                >
                  {aiBusy ? "GENERATING..." : isPro ? "🪄 GENERATE" : "UNLOCK WITH PRO"}
                </button>
              </div>
            </div>

            {pfpUrl && (
              <div className="flex items-center gap-4">
                <span className="text-xs text-[#a3a3ac]">Current custom PFP:</span>
                <AvatarChip avatarId={null} pfpUrl={pfpUrl} size={56} />
                <button
                  onClick={() => setPfpUrl(null)}
                  data-testid="profile-pfp-clear"
                  className="text-xs text-[#14b8a6] hover:underline"
                >
                  remove
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "details" && (
          <div className="space-y-4" data-testid="profile-tab-content-details">
            <DisplayNameEditor user={user} onUpdated={setUser} onToast={onSaved} />
            <div>
              <label className="block text-xs tracking-widest text-[#14b8a6] mb-1">BIO</label>
              <textarea
                data-testid="profile-bio-input"
                rows="3"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
                placeholder="A short intro for league owners..."
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest text-[#14b8a6] mb-1">PRIMARY GAME</label>
              <select
                data-testid="profile-game-select"
                value={game}
                onChange={(e) => setGame(e.target.value)}
                className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
              >
                {games.map((g) => (
                  <option key={g.slug} value={g.name}>{g.name} • {g.category}</option>
                ))}
              </select>
            </div>
            <div data-testid="profile-games-played">
              <label className="block text-xs tracking-widest text-[#14b8a6] mb-1">
                ALL GAMES I PLAY <span className="text-white/40 normal-case">— tap to toggle, max 12</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {games.map((g) => {
                  const on = gamesPlayed.includes(g.name);
                  return (
                    <button
                      type="button"
                      key={g.slug}
                      onClick={() => {
                        setGamesPlayed((prev) =>
                          on ? prev.filter((x) => x !== g.name) :
                            prev.length >= 12 ? prev : [...prev, g.name]
                        );
                      }}
                      data-testid={`profile-game-chip-${g.slug}`}
                      className={`text-[11px] px-2.5 py-1 rounded-lg border transition ${
                        on
                          ? "bg-[#14b8a6]/15 border-[#14b8a6] text-[#14b8a6]"
                          : "bg-black/40 border-white/15 text-white/55 hover:border-[#14b8a6]/40"
                      }`}
                    >
                      {on ? <i className="fas fa-check mr-1" /> : null}{g.name}
                    </button>
                  );
                })}
                <AddGameChip
                  onCreated={(created) => {
                    setGames((prev) =>
                      prev.some((g) => g.slug === created.slug)
                        ? prev
                        : [...prev, created],
                    );
                    setGamesPlayed((prev) =>
                      prev.includes(created.name) || prev.length >= 12
                        ? prev
                        : [...prev, created.name],
                    );
                    onToast?.(`🎮 Added "${created.name}".`);
                  }}
                  onToast={onToast}
                />
              </div>
              <p className="text-[10px] text-white/50 mt-2">
                <i className="fas fa-info-circle mr-1" />Fair-play rule: you can only join one league + one team per game.
              </p>
            </div>
            <div className="bg-black/30 rounded-2xl p-3 border border-[#14b8a6]/20 space-y-2" data-testid="profile-messaging-policy">
              <p className="text-xs font-bold tracking-wider">
                <i className="fas fa-envelope mr-1 text-[#14b8a6]" />WHO CAN DM YOU?
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { code: "everyone", label: "EVERYONE", emoji: "🌐", hint: "Open mailbox" },
                  { code: "friends",  label: "FRIENDS",  emoji: "🤝", hint: "Friends-only" },
                  { code: "off",      label: "OFF",      emoji: "🔕", hint: "No DMs" },
                ].map((opt) => {
                  const active = messagingPolicy === opt.code;
                  return (
                    <button
                      key={opt.code}
                      type="button"
                      onClick={() => { setMessagingPolicy(opt.code); setMessagingEnabled(opt.code !== "off"); }}
                      data-testid={`profile-messaging-${opt.code}`}
                      aria-pressed={active}
                      title={opt.hint}
                      className={`px-2 py-2 rounded-xl border text-[10px] tracking-widest font-bold transition ${
                        active
                          ? "bg-[#14b8a6]/15 border-[#14b8a6] text-[#14b8a6] shadow-[0_0_12px_rgba(20, 184, 166,0.4)]"
                          : "bg-black/50 border-white/15 text-white/60 hover:text-white"
                      }`}
                    >
                      <div className="text-base">{opt.emoji}</div>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-white/55">
                {messagingPolicy === "everyone" && "Anyone can DM you — fastest way to get pinged for squads."}
                {messagingPolicy === "friends" && "Only friends can DM. Others must send a 🤝 friend request first."}
                {messagingPolicy === "off" && "DMs are closed. Your MSG button is hidden from your card."}
              </p>
            </div>
            <p className="text-[11px] text-white/50">
              Add your Discord, X, Telegram or Twitch in the SOCIAL tab.
            </p>

            {/* Self-attestation badges (Phase 43) — opt-in cosmetic flair
                that surfaces on the player profile card. Mutually
                exclusive: turning one on auto-flips the other off. No
                verification is performed; it's a fun identity toggle. */}
            <div
              data-testid="profile-attestation-badges"
              className="bg-black/30 rounded-2xl p-3 border border-[#14b8a6]/20 space-y-2"
            >
              <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold">
                <i className="fas fa-id-badge mr-1" />SELF-ATTESTATION BADGES
              </p>
              <p className="text-[11px] text-white/55">
                Toggle one on to display a chip on your profile card.
                100% optional flair — no proof required.
              </p>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => {
                    const next = !isCertifiedDad;
                    setIsCertifiedDad(next);
                    if (next) setIsCertifiedMom(false);
                  }}
                  data-testid="profile-toggle-certified-dad"
                  aria-pressed={isCertifiedDad}
                  className={`text-left rounded-xl border px-3 py-3 transition-colors ${
                    isCertifiedDad
                      ? "bg-[#14b8a6]/20 border-[#14b8a6]/60"
                      : "bg-black/40 border-white/10 hover:border-[#14b8a6]/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className={`text-[11px] font-bold tracking-widest ${isCertifiedDad ? "text-[#14b8a6]" : "text-white"}`}>
                      <i className="fas fa-mug-hot mr-1" />CERTIFIED DAD
                    </p>
                    <span className={`w-3 h-3 rounded-full ${isCertifiedDad ? "bg-[#14b8a6]" : "bg-white/15"}`} />
                  </div>
                  <p className="text-[10px] text-white/55 mt-1 leading-snug">
                    "Just one more match, kids are asleep."
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = !isCertifiedMom;
                    setIsCertifiedMom(next);
                    if (next) setIsCertifiedDad(false);
                  }}
                  data-testid="profile-toggle-certified-mom"
                  aria-pressed={isCertifiedMom}
                  className={`text-left rounded-xl border px-3 py-3 transition-colors ${
                    isCertifiedMom
                      ? "bg-[#14b8a6]/20 border-[#14b8a6]/60"
                      : "bg-black/40 border-white/10 hover:border-[#14b8a6]/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className={`text-[11px] font-bold tracking-widest ${isCertifiedMom ? "text-[#14b8a6]" : "text-white"}`}>
                      <i className="fas fa-heart mr-1" />CERTIFIED MOM
                    </p>
                    <span className={`w-3 h-3 rounded-full ${isCertifiedMom ? "bg-[#14b8a6]" : "bg-white/15"}`} />
                  </div>
                  <p className="text-[10px] text-white/55 mt-1 leading-snug">
                    "Crit chance & snack supply at 100%."
                  </p>
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "social" && (
          <div className="space-y-4" data-testid="profile-tab-content-social">
            <p className="text-[10px] tracking-widest text-[#14b8a6]">🔗 SOCIAL HANDLES • how teammates ping you</p>

            {/* Profile banner — PRO ONLY. Free users see a locked upgrade prompt. */}
            <div data-testid="profile-banner-section">
              <label className="block text-xs tracking-widest text-[#14b8a6] mb-1">
                <i className="fas fa-image mr-1" /> PROFILE BANNER {!isPro && <span className="text-[#14b8a6]">• PRO ONLY</span>}
              </label>
              {isPro ? (
                <>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="profile-banner-input"
                      value={bannerUrl?.startsWith("data:") ? "" : bannerUrl}
                      onChange={(e) => setBannerUrl(e.target.value)}
                      placeholder="https://… (or upload below)"
                      className="flex-1 bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
                    />
                    <label
                      data-testid="profile-banner-upload"
                      className="cursor-pointer text-[11px] tracking-widest px-3 py-3 rounded-2xl bg-black/40 border border-[#14b8a6]/40 text-[#14b8a6] hover:bg-[#14b8a6]/10"
                    >
                      <i className="fas fa-upload mr-1.5" />UPLOAD
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          if (f.size > 3_000_000) { onSaved?.("Banner too large (max 3 MB)."); return; }
                          const r = new FileReader();
                          r.onload = () => setBannerUrl(r.result);
                          r.readAsDataURL(f);
                        }}
                        className="hidden"
                      />
                    </label>
                    {bannerUrl && (
                      <button
                        type="button"
                        onClick={() => setBannerUrl("")}
                        data-testid="profile-banner-clear"
                        className="text-[10px] tracking-widest px-2.5 py-3 rounded-2xl btn-ghost"
                      >
                        <i className="fas fa-xmark" />
                      </button>
                    )}
                  </div>
                  {bannerUrl && (
                    <div
                      data-testid="profile-banner-preview"
                      className="mt-2 h-20 rounded-2xl border border-[#14b8a6]/30"
                      style={{ backgroundImage: `url(${bannerUrl})`, backgroundSize: "cover", backgroundPosition: "center" }}
                    />
                  )}
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => onRequireUpgrade?.()}
                  data-testid="profile-banner-pro-lock"
                  className="w-full text-left rounded-2xl p-4 bg-gradient-to-r from-[#14b8a6]/15 to-[#14b8a6]/10 border border-[#14b8a6]/40 hover:border-[#14b8a6] transition"
                >
                  <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
                    <i className="fas fa-lock mr-1" />PRO PERK • $1.99/MO
                  </p>
                  <p className="text-sm text-white/85 mt-1">
                    Customize a hero banner above your operator card. Unlock with PRO Player.
                  </p>
                  <p className="text-[11px] text-[#14b8a6] mt-2 font-bold">
                    UNLOCK WITH PRO →
                  </p>
                </button>
              )}
            </div>

            <div>
              <label className="block text-xs tracking-widest text-[#14b8a6] mb-1">
                <i className="fas fa-link mr-1" /> WEBSITE
              </label>
              <input
                data-testid="profile-website-input"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://yourpage.gg"
                className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest text-[#ff0000]/90 mb-1">
                <i className="fab fa-youtube mr-1" /> YOUTUBE · @HANDLE ONLY
              </label>
              <input
                data-testid="profile-youtube-input"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="@neondrift"
                className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
              />
            </div>

            <div>
              <label className="block text-xs tracking-widest text-[#5865F2] mb-1">
                <i className="fab fa-discord mr-1" /> DISCORD · @HANDLE ONLY
              </label>
              <input
                data-testid="profile-discord-input"
                value={discordHandle}
                onChange={(e) => setDiscordHandle(e.target.value)}
                placeholder="@neondrift"
                className="w-full bg-black/40 border border-[#5865F2]/40 rounded-2xl px-4 py-3 text-white"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest text-white mb-1">
                <i className="fab fa-x-twitter mr-1" /> X (TWITTER) · @HANDLE ONLY
              </label>
              <input
                data-testid="profile-x-input"
                value={xHandle}
                onChange={(e) => setXHandle(e.target.value)}
                placeholder="@neondrift"
                className="w-full bg-black/40 border border-white/30 rounded-2xl px-4 py-3 text-white"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest text-[#14b8a6] mb-1">
                <i className="fab fa-telegram mr-1" /> TELEGRAM · @HANDLE ONLY
              </label>
              <input
                data-testid="profile-telegram-input"
                value={telegramHandle}
                onChange={(e) => setTelegramHandle(e.target.value)}
                placeholder="@neondrift"
                className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest text-[#9146FF] mb-1">
                <i className="fab fa-twitch mr-1" /> TWITCH · @HANDLE ONLY
              </label>
              <input
                data-testid="profile-twitch-input"
                value={twitchHandle}
                onChange={(e) => setTwitchHandle(e.target.value)}
                placeholder="@neondrift"
                className="w-full bg-black/40 border border-[#9146FF]/40 rounded-2xl px-4 py-3 text-white"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest text-[#53FC18] mb-1">
                <i className="fas fa-tower-broadcast mr-1" /> KICK · @HANDLE ONLY
              </label>
              <input
                data-testid="profile-kick-input"
                value={kickUrl}
                onChange={(e) => setKickUrl(e.target.value)}
                placeholder="@adinross"
                className="w-full bg-black/40 border border-[#53FC18]/40 rounded-2xl px-4 py-3 text-white"
              />
            </div>

            {/* Streamer Mode toggle — when ON, we poll Twitch/YouTube/Kick every
                10 min and surface a red LIVE pill on the profile + holo card. */}
            <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="profile-streamer-mode-toggle"
                  checked={streamerMode}
                  onChange={(e) => setStreamerMode(e.target.checked)}
                  className="w-4 h-4 accent-red-500"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold tracking-wider text-red-400">
                    <i className="fas fa-circle text-red-500 animate-pulse mr-1.5" />
                    STREAMER MODE
                  </p>
                  <p className="text-[10px] text-white/60 leading-relaxed mt-0.5">
                    We&apos;ll auto-detect when you go live on Twitch / YouTube / Kick
                    and show a LIVE badge on your profile + holo card.
                  </p>
                </div>
              </label>
            </div>
            <p className="text-[10px] text-white/40 leading-relaxed -mt-1">
              <i className="fas fa-shield-halved mr-1 text-[#14b8a6]" />
              Handles only — pasting a full URL is rejected. We build the link
              automatically (e.g. <span className="text-[#14b8a6]">youtube.com/@you</span>).
              This protects the community from inappropriate external links.
            </p>

            <div className="pt-3 mt-2 border-t border-white/10">
              <p className="text-[10px] tracking-widest text-[#14b8a6] mb-3">
                <i className="fas fa-gamepad mr-1" />GAMERTAGS · stack as many as you play on
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: "steam", label: "STEAM", icon: "fab fa-steam", color: "border-[#14b8a6]/40 text-[#14b8a6]", placeholder: "Steam name / SteamID", val: steamHandle, set: setSteamHandle, errKey: "steam_handle" },
                  { id: "battlenet", label: "BATTLE.NET", icon: "fab fa-battle-net", color: "border-blue-400/40 text-blue-200", placeholder: "BattleTag#1234", val: battlenetHandle, set: setBattlenetHandle, errKey: "battlenet_handle" },
                  { id: "riot", label: "RIOT ID", icon: "fas fa-bolt", color: "border-[#14b8a6]/40 text-[#14b8a6]", placeholder: "Name#TAG", val: riotHandle, set: setRiotHandle, errKey: "riot_handle" },
                  { id: "psn", label: "PSN", icon: "fab fa-playstation", color: "border-[#14b8a6]/40 text-[#14b8a6]", placeholder: "PSN ID", val: psnHandle, set: setPsnHandle, errKey: "psn_handle" },
                  { id: "xbox", label: "XBOX LIVE", icon: "fab fa-xbox", color: "border-[#14b8a6]/40 text-[#14b8a6]", placeholder: "Gamertag", val: xboxHandle, set: setXboxHandle, errKey: "xbox_handle" },
                  { id: "epic", label: "EPIC", icon: "fas fa-cube", color: "border-slate-300/40 text-slate-200", placeholder: "Epic display name", val: epicHandle, set: setEpicHandle, errKey: "epic_handle" },
                ].map((f) => {
                  const err = platformErrors[f.errKey];
                  return (
                  <div key={f.id}>
                    <label className={`block text-[10px] tracking-widest ${f.color.split(" ")[1]} mb-1`}>
                      <i className={`${f.icon} mr-1`} /> {f.label}
                    </label>
                    <input
                      data-testid={`profile-${f.id}-input`}
                      value={f.val}
                      onChange={(e) => {
                        f.set(e.target.value);
                        if (err) {
                          // Clear the inline error the moment the user
                          // starts editing the offending field.
                          setPlatformErrors((prev) => {
                            const { [f.errKey]: _, ...rest } = prev;
                            return rest;
                          });
                        }
                      }}
                      placeholder={f.placeholder}
                      maxLength={64}
                      className={`w-full bg-black/40 border ${err ? "border-red-500/70" : f.color.split(" ")[0]} rounded-2xl px-3 py-2.5 text-white text-sm`}
                    />
                    {err && (
                      <p
                        data-testid={`profile-${f.id}-error`}
                        className="mt-1 text-[11px] text-red-400 flex items-center gap-1"
                      >
                        <i className="fas fa-circle-exclamation" />
                        <span>{err}</span>
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-3 mt-2 border-t border-white/10">
              <label className="block text-xs tracking-widest text-white/60 mb-1">
                <i className="fas fa-link mr-1" /> OTHER LINK
              </label>
              <input
                data-testid="profile-social-input"
                value={social}
                onChange={(e) => setSocial(e.target.value)}
                placeholder="https://your.site"
                className="w-full bg-black/40 border border-white/20 rounded-2xl px-4 py-3 text-white"
              />
            </div>
          </div>
        )}

        {tab === "skill" && (
          <div className="space-y-5" data-testid="profile-tab-content-skill">
            <div>
              <label className="block text-xs tracking-widest text-[#14b8a6] mb-1">OPERATOR TYPE</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setUserType("casual")}
                  data-testid="profile-type-casual"
                  className={`py-3 rounded-2xl font-bold tracking-widest ${
                    userType === "casual" ? "btn-neon" : "bg-black/40 border border-white/15 text-white"
                  }`}
                >
                  CASUAL
                </button>
                <button
                  onClick={() => { if (isPro) setUserType("pro"); else onRequireUpgrade?.(); }}
                  data-testid="profile-type-pro"
                  className={`py-3 rounded-2xl font-bold tracking-widest ${
                    userType === "pro" ? "btn-neon" : "bg-black/40 border border-white/15 text-white"
                  }`}
                >
                  PRO {!isPro && <span className="text-xs">• $1.99/mo</span>}
                </button>
              </div>
            </div>
            <div
              data-testid="profile-mmr-info"
              className="rounded-2xl px-4 py-3 border border-[#14b8a6]/30 bg-[#14b8a6]/5"
            >
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
                <i className="fas fa-chart-line mr-1" />SKILL · AUTO-CALCULATED
              </p>
              <p className="text-[11px] text-white/65 mt-1">
                Your MMR / tier (Bronze → Champion) is computed from real match results.
                Win games to climb — no self-proclaimed rating.
              </p>
            </div>
          </div>
        )}

        {tab === "glory" && (
          <div data-testid="profile-tab-content-glory">
            <GloryBadgesGrid mine onToast={onSaved} defaultExpanded />
          </div>
        )}

        {tab === "alerts" && (
          <div data-testid="profile-tab-content-alerts">
            <NotificationSettings onToast={onSaved} />
          </div>
        )}

        {tab === "inbox" && (
          <div data-testid="profile-tab-content-inbox">
            <InboxPanel onToast={onSaved} />
          </div>
        )}

        {tab === "help" && (
          <div data-testid="profile-tab-content-help">
            <SupportTicketsPanel onToast={onSaved} />
          </div>
        )}

        {tab === "privacy" && (
          <div data-testid="profile-tab-content-privacy">
            <PrivacySection
              onAccountDeleted={() => {
                // Server already cleared the session cookie + terminated
                // the row. Best-effort hard reload so the AuthContext
                // forgets the user and the marketing Hero re-renders.
                window.location.assign("/");
              }}
            />
          </div>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} data-testid="profile-cancel-btn" className="btn-ghost px-6 py-3 rounded-2xl">
            {(tab === "inbox" || tab === "glory" || tab === "alerts" || tab === "help" || tab === "privacy") ? "Close" : "Cancel"}
          </button>
          {tab !== "inbox" && tab !== "glory" && tab !== "alerts" && tab !== "help" && tab !== "privacy" && (
            <button
              onClick={save}
              disabled={saving}
              data-testid="profile-save-btn"
              className="btn-neon px-8 py-3 rounded-2xl disabled:opacity-60"
            >
              {saving ? "SAVING..." : "SAVE PROFILE"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePanel;

// ---------------------------------------------------------------------------
// Display name editor — free for everyone with a 30-day cooldown to prevent
// abuse. Optional opt-in history visibility on the public profile card.
// ---------------------------------------------------------------------------
function DisplayNameEditor({ user, onUpdated, onToast }) {
  const [draft, setDraft] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const lastAt = user?.name_changed_at ? new Date(user.name_changed_at) : null;
  const nextAt = lastAt ? new Date(lastAt.getTime() + 30 * 24 * 60 * 60 * 1000) : null;
  const cooldownActive = !!nextAt && nextAt > new Date();
  const daysLeft = cooldownActive ? Math.ceil((nextAt - new Date()) / (1000 * 60 * 60 * 24)) : 0;
  const history = (user?.name_history || []).filter((h) => h?.name && h.name !== (user?.provider_name || ""));
  const showHistoryPublic = user?.name_history_public === true;

  const submit = async (e) => {
    e.preventDefault();
    const next = (draft || "").trim();
    if (!next || next === user?.name) return;
    setBusy(true);
    try {
      const u = await changeDisplayName(next);
      onUpdated?.(u);
      onToast?.(`✏️ Display name changed to ${u.name}.`);
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Name change failed.");
    } finally { setBusy(false); }
  };

  const onToggleHistory = async (next) => {
    try {
      const u = await toggleNameHistoryVisibility(next);
      onUpdated?.(u);
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Could not save preference.");
    }
  };

  return (
    <div className="bg-black/30 border border-[#14b8a6]/25 rounded-2xl p-3 space-y-2" data-testid="profile-display-name-section">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-id-badge mr-1" />DISPLAY NAME
        </p>
        <span className="text-[9px] tracking-[3px] text-[#14b8a6]">FREE · ONCE/MO</span>
      </div>
      <form onSubmit={submit} className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={40}
          minLength={2}
          disabled={cooldownActive || busy}
          data-testid="profile-name-input"
          className="flex-1 bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={cooldownActive || busy || !draft.trim() || draft === user?.name}
          data-testid="profile-name-save-btn"
          className="text-[10px] tracking-widest px-3 py-2 rounded-xl btn-neon disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "..." : "SAVE"}
        </button>
      </form>
      {cooldownActive ? (
        <p className="text-[10px] text-[#14b8a6]/80" data-testid="profile-name-cooldown">
          ⏳ Next change available in <b>{daysLeft}</b> day{daysLeft === 1 ? "" : "s"}.
        </p>
      ) : (
        <p className="text-[10px] text-white/50">
          Free name change, limited to once every 30 days. Your OG sign-in name is never shown in history.
        </p>
      )}

      {history.length > 0 && (
        <div className="border-t border-white/10 pt-2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              data-testid="profile-name-history-toggle"
              className="text-[10px] tracking-widest text-[#a3a3ac] hover:text-[#14b8a6]"
            >
              <i className={`fas ${historyOpen ? "fa-chevron-down" : "fa-chevron-right"} mr-1`} />
              PREVIOUS NAMES ({history.length})
            </button>
            <label className="flex items-center gap-1.5 text-[10px] text-white/60 cursor-pointer">
              <span>SHOW PUBLICLY</span>
              <input
                type="checkbox"
                checked={showHistoryPublic}
                onChange={(e) => onToggleHistory(e.target.checked)}
                data-testid="profile-name-history-visibility"
                className="accent-[#14b8a6]"
              />
            </label>
          </div>
          {historyOpen && (
            <ul className="space-y-1" data-testid="profile-name-history-list">
              {history.slice().reverse().map((h, i) => (
                <li key={i} className="text-[11px] text-white/70 flex justify-between items-center">
                  <span className="truncate">{h.name}</span>
                  <span className="text-white/35 text-[10px] ml-2 whitespace-nowrap">
                    {new Date(h.changed_at).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * AddGameChip — inline-expandable chip that lives at the end of the
 * games_played grid. Click → reveals a name + category input → POST
 * /api/games → the new game is added globally (so every other player
 * sees it too) and auto-toggled on the caller's profile.
 *
 * Lighter-weight than the standalone GameSelect because it doesn't
 * need the dropdown / "all games" affordance — the grid IS the
 * selector. We only need the "add" path.
 */
function AddGameChip({ onCreated, onToast }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Other");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      onToast?.("Game name too short.");
      return;
    }
    setBusy(true);
    try {
      const created = await createGame({ name: trimmed, category });
      onCreated?.(created);
      setOpen(false);
      setName("");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't add the game.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="profile-add-game-chip"
        className="text-[11px] px-2.5 py-1 rounded-lg border border-dashed border-violet-400/50 text-violet-300 hover:bg-violet-500/10 transition"
      >
        <i className="fas fa-plus mr-1" />ADD GAME
      </button>
    );
  }

  return (
    <div
      data-testid="profile-add-game-form"
      className="basis-full flex flex-wrap items-center gap-2 mt-1 p-2 rounded-xl border border-violet-400/30 bg-black/30"
    >
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder="Game name (e.g. Marvel Snap)"
        maxLength={48}
        data-testid="profile-add-game-name"
        className="flex-1 min-w-[160px] bg-black/40 border border-violet-400/40 rounded-lg px-3 py-1.5 text-white text-xs"
      />
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        data-testid="profile-add-game-category"
        className="bg-black/40 border border-violet-400/30 rounded-lg px-2 py-1.5 text-white text-xs"
      >
        {["MOBA", "FPS", "Battle Royale", "Sports", "Racing", "Fighting",
          "Strategy", "Card", "Auto Battler", "Mobile", "IRL Sports",
          "Other"].map((c) => (
          <option key={c}>{c}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={submit}
        disabled={busy}
        data-testid="profile-add-game-save"
        className="text-[10px] tracking-widest font-bold px-3 py-1.5 rounded-lg bg-violet-500 text-white hover:brightness-110 disabled:opacity-50"
      >
        {busy ? "…" : "ADD"}
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setName(""); }}
        data-testid="profile-add-game-cancel"
        className="text-[10px] tracking-widest text-white/60 hover:text-white"
      >
        CANCEL
      </button>
    </div>
  );
}
